/**
 * Voice Input — Web Speech API wrapper for TERRA.
 *
 * Uses the browser's built-in SpeechRecognition API (Chrome, Edge, Safari).
 * No external dependencies. Falls back gracefully if unsupported.
 *
 * Usage:
 *   const stop = startListening(
 *     (text) => setInput(text),
 *     (err) => console.error(err)
 *   );
 *   // later: stop()
 */

const SpeechRecognition =
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export function isVoiceSupported(): boolean {
  return !!SpeechRecognition;
}

/**
 * Start listening for speech input.
 *
 * @param onResult - Called with the transcript text (interim and final)
 * @param onError - Called with error message
 * @param onEnd - Called when recognition stops (natural end or error)
 * @returns stop function to manually stop listening
 */
export function startListening(
  onResult: (text: string, isFinal: boolean) => void,
  onError: (err: string) => void,
  onEnd?: () => void,
): () => void {
  if (!SpeechRecognition) {
    onError('Voice input not supported in this browser. Try Chrome or Edge.');
    onEnd?.();
    return () => {};
  }

  const recognition = new SpeechRecognition();
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.lang = 'en-US';
  recognition.maxAlternatives = 1;

  recognition.onresult = (event: any) => {
    let transcript = '';
    let isFinal = false;
    for (let i = event.resultIndex; i < event.results.length; i++) {
      transcript += event.results[i][0].transcript;
      if (event.results[i].isFinal) isFinal = true;
    }
    onResult(transcript, isFinal);
  };

  recognition.onerror = (event: any) => {
    const messages: Record<string, string> = {
      'no-speech': 'No speech detected. Try again.',
      'audio-capture': 'No microphone found. Check your audio settings.',
      'not-allowed': 'Microphone access denied. Allow microphone in browser settings.',
      'network': 'Network error during speech recognition.',
    };
    onError(messages[event.error] || `Speech error: ${event.error}`);
  };

  recognition.onend = () => {
    onEnd?.();
  };

  recognition.start();

  return () => {
    try {
      recognition.stop();
    } catch {
      // already stopped
    }
  };
}
